import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Card, SectionTitle, ProgressBar, Ring } from "./shared";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, TrendingUp, CalendarClock, Gauge, Layers } from "lucide-react";
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from "recharts";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);
const mxn2 = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(n || 0);
const pct = (p) => `${((p || 0) * 100).toFixed(2)}%`;

const SEM = {
  verde: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", txt: "En línea con el programa" },
  amarillo: { cls: "bg-amber-50 text-amber-700 border-amber-200", txt: "Ligero atraso vs programa" },
  rojo: { cls: "bg-red-50 text-red-700 border-red-200", txt: "Atraso significativo vs programa" },
  sin_plan: { cls: "bg-slate-50 text-slate-500 border-slate-200", txt: "Sin fechas de contrato para comparar" },
};
const PROY = {
  en_tiempo: { cls: "text-emerald-600", txt: "Terminaría en tiempo" },
  riesgo: { cls: "text-amber-600", txt: "En riesgo (≤30 días de desfase)" },
  retrasado: { cls: "text-red-600", txt: "Terminaría fuera de plazo" },
  sin_fin_programado: { cls: "text-slate-500", txt: "Captura el fin programado del contrato" },
  sin_datos: { cls: "text-slate-500", txt: "Faltan estimaciones para proyectar" },
  terminada: { cls: "text-emerald-600", txt: "Obra al 100%" },
};
const ESTADO = {
  sin_iniciar: "bg-slate-100 text-slate-500",
  en_proceso: "bg-cyan-50 text-cyan-700",
  terminada: "bg-emerald-50 text-emerald-700",
};

function Kpi({ titulo, valor, sub, testId, color = "text-[#002B49]" }) {
  return (
    <Card className="p-3" testId={testId}>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{titulo}</p>
      <p className={`font-display text-lg font-bold ${color}`}>{valor}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}

function PartidaFila({ p }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-slate-100 bg-[#002B49]/[0.03]" data-testid={`control-partida-${p.id}`}>
        <td className="px-3 py-2">
          <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 font-bold text-[#002B49] text-left"
            data-testid={`control-partida-toggle-${p.id}`}>
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {p.nombre}
            <span className="text-[10px] font-normal text-slate-400">({p.subpartidas.length})</span>
          </button>
        </td>
        <td className="px-3 py-2 text-right text-slate-600">{mxn(p.presupuesto)}</td>
        <td className="px-3 py-2 text-right font-semibold text-[#00A3B5]">{mxn(p.ejecutado)}</td>
        <td className="px-3 py-2 text-right text-slate-500">{mxn(p.por_ejecutar)}</td>
        <td className="px-3 py-2 text-right text-slate-500">{mxn(p.ultimo_periodo_importe)}</td>
        <td className="px-3 py-2 w-[180px]">
          <div className="flex items-center gap-2">
            <ProgressBar value={Math.min((p.pct_avance || 0) * 100, 100)} className="flex-1" />
            <span className="text-xs font-bold text-[#002B49] w-14 text-right">{pct(p.pct_avance)}</span>
          </div>
        </td>
      </tr>
      {open && p.subpartidas.map((s) => (
        <tr key={s.id} className="border-b border-slate-50 text-xs" data-testid={`control-sub-${s.id}`}>
          <td className="px-3 py-1.5 pl-9 text-slate-600">
            {s.nombre}
            {s.es_extra && <span className="ml-2 text-[9px] font-bold text-white bg-violet-500 px-1.5 py-0.5 rounded uppercase">Extra</span>}
            <span className={`ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${ESTADO[s.estado]}`}>
              {s.estado === "sin_iniciar" ? "Sin iniciar" : s.estado === "terminada" ? "Terminada" : "En proceso"}
            </span>
          </td>
          <td className="px-3 py-1.5 text-right text-slate-500">{mxn(s.presupuesto)}</td>
          <td className="px-3 py-1.5 text-right text-slate-600">{mxn(s.ejecutado)}</td>
          <td className="px-3 py-1.5 text-right text-slate-400">{mxn(s.por_ejecutar)}</td>
          <td className="px-3 py-1.5 text-right text-slate-400">{mxn(s.ultimo_periodo_importe)}</td>
          <td className="px-3 py-1.5">
            <div className="flex items-center gap-2">
              <ProgressBar value={Math.min((s.pct_avance || 0) * 100, 100)} className="flex-1" />
              <span className="text-[11px] text-slate-500 w-14 text-right">{pct(s.pct_avance)}</span>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

export default function ControlObra({ pid }) {
  const [contratos, setContratos] = useState(null);
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/properties/${pid}/contratos-obra`).then((r) => {
      setContratos(r.data);
      if (r.data.length) setSel(r.data[0].id);
    }).catch((e) => toast.error(apiError(e)));
  }, [pid]);

  useEffect(() => {
    if (!sel) return;
    setData(null);
    api.get(`/contratos-obra/${sel}/control`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [sel]);

  if (contratos === null) return <p className="text-sm text-slate-400 py-8">Cargando…</p>;
  if (!contratos.length) return (
    <Card className="p-10 text-center" testId="control-vacio">
      <Gauge size={36} className="mx-auto text-slate-300 mb-3" />
      <p className="text-slate-500 font-semibold mb-1">Sin contratos de obra</p>
      <p className="text-xs text-slate-400">Crea un contrato y confirma su primera estimación en la pestaña «Estimaciones (IA)» para ver el tablero de control.</p>
    </Card>
  );

  const g = data?.global;
  const pr = data?.proyeccion;
  const sem = SEM[g?.semaforo || "sin_plan"];
  const proy = PROY[pr?.estatus || "sin_datos"];
  const curva = (data?.curva_s || []).map((x) => ({
    etiqueta: x.etiqueta, real: x.real_pct == null ? null : +(x.real_pct * 100).toFixed(2),
    programado: x.programado_pct == null ? null : +(x.programado_pct * 100).toFixed(2),
    importe: x.real_importe, ministrado: x.ministrado,
  }));
  const ranking = [...(data?.partidas || [])].filter((p) => p.presupuesto > 0)
    .sort((a, b) => a.pct_avance - b.pct_avance);

  return (
    <div data-testid="control-obra-tab">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Contrato / contratista</Label>
          <Select value={sel || ""} onValueChange={setSel}>
            <SelectTrigger className="w-64" data-testid="control-contrato-select"><SelectValue /></SelectTrigger>
            <SelectContent>{contratos.map((c) => <SelectItem key={c.id} value={c.id}>{c.contratista}{c.nombre_proyecto ? ` — ${c.nombre_proyecto}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {data && (
          <div className={`border rounded-lg px-3 py-2 text-xs font-semibold ${sem.cls}`} data-testid="control-semaforo">
            {sem.txt}
            {g.avance_programado_pct != null && <span className="font-normal ml-1">· real {pct(g.pct_avance)} vs programado {pct(g.avance_programado_pct)}
              {g.desviacion_pct != null && ` (${g.desviacion_pct >= 0 ? "+" : ""}${(g.desviacion_pct * 100).toFixed(2)} pts)`}</span>}
          </div>
        )}
      </div>

      {!data ? <p className="text-sm text-slate-400 py-8">Calculando métricas…</p> : g.num_estimaciones === 0 ? (
        <Card className="p-10 text-center" testId="control-sin-estimaciones">
          <Layers size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-semibold mb-1">Este contrato aún no tiene estimaciones confirmadas</p>
          <p className="text-xs text-slate-400">Sube el Excel del contratista en «Estimaciones (IA)» para alimentar el tablero.</p>
        </Card>
      ) : (
        <>
          <Card className="p-5 mb-4" testId="control-resumen">
            <div className="flex flex-wrap items-center gap-6">
              <Ring value={+Math.min((g.pct_avance || 0) * 100, 100).toFixed(1)} size={96} label="Avance" />
              <div className="flex-1 min-w-[220px]">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Avance financiero de la obra</p>
                <p className="font-display text-2xl font-bold text-[#002B49]" data-testid="control-avance">{pct(g.pct_avance)}</p>
                <p className="text-xs text-slate-500 mt-1">{mxn2(g.ejecutado)} ejecutados de {mxn2(g.presupuesto)} · {g.num_estimaciones} estimación(es) · última {fmtDate(g.ultima_fecha)}</p>
                <div className="mt-2"><ProgressBar value={Math.min((g.pct_avance || 0) * 100, 100)} /></div>
              </div>
              <div className="border-l border-slate-200 pl-6 min-w-[240px]">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold flex items-center gap-1"><CalendarClock size={12} /> Proyección de terminación</p>
                <p className={`font-display text-xl font-bold ${proy.cls}`} data-testid="control-proyeccion-fecha">
                  {pr.fecha_estimada ? fmtDate(pr.fecha_estimada) : "—"}
                </p>
                <p className={`text-xs font-semibold ${proy.cls}`} data-testid="control-proyeccion-estatus">{proy.txt}</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  {pr.ritmo_mensual_pct ? `Ritmo ${(pr.ritmo_mensual_pct * 100).toFixed(2)}%/mes (${mxn(pr.ritmo_mensual_importe)}/mes)` : "Sin ritmo medible"}
                  {pr.meses_restantes != null && ` · faltan ${pr.meses_restantes} meses`}
                  {pr.fin_programado && ` · programado ${fmtDate(pr.fin_programado)}`}
                  {pr.dias_desviacion != null && ` · ${pr.dias_desviacion > 0 ? `+${pr.dias_desviacion} días de desfase` : `${Math.abs(pr.dias_desviacion)} días de holgura`}`}
                </p>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Kpi titulo="Presupuesto contratado" valor={mxn2(g.presupuesto)} testId="control-kpi-presupuesto" />
            <Kpi titulo="Por ejecutar" valor={mxn2(g.por_ejecutar)} sub={`${pct(1 - (g.pct_avance || 0))} del contrato`} testId="control-kpi-por-ejecutar" />
            <Kpi titulo="Ministrado acumulado" valor={mxn2(g.ministrado)} sub={`Sin ejecutar: ${mxn2(g.ministrado_sin_ejecutar)}`}
              color={g.ministrado_sin_ejecutar < 0 ? "text-red-600" : "text-[#002B49]"} testId="control-kpi-ministrado" />
            <Kpi titulo="Último periodo estimado" valor={mxn2(g.ultimo_periodo_importe)} sub={`${g.subpartidas_sin_iniciar} de ${g.subpartidas_total} subpartidas sin iniciar`} testId="control-kpi-ultimo" />
          </div>

          <Card className="p-5 mb-4" testId="control-curva-s">
            <SectionTitle right={<span className="text-[11px] text-slate-400 flex items-center gap-1"><TrendingUp size={12} /> % acumulado del presupuesto</span>}>
              Curva S — avance real vs programado
            </SectionTitle>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={curva} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#EEF2F6" vertical={false} />
                <XAxis dataKey="etiqueta" tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 10, fill: "#64748B" }} width={44} />
                <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  formatter={(v, n) => [v == null ? "—" : `${v}%`, n === "real" ? "Real acumulado" : "Programado"]} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "real" ? "Real acumulado" : "Programado (lineal)")} />
                <Area type="linear" dataKey="real" stroke="#00A3B5" fill="#00A3B5" fillOpacity={0.14} strokeWidth={2.5} connectNulls
                  dot={{ r: 3, fill: "#002B49" }} />
                <Line type="linear" dataKey="programado" stroke="#C9A96A" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
            {!data.contrato.fecha_fin_programada && (
              <p className="text-[11px] text-amber-600 mt-1">Captura «Fecha inicio» y «Fin programado» en el contrato para ver la curva programada y el semáforo.</p>
            )}
          </Card>

          <Card className="overflow-hidden mb-4" testId="control-tabla-partidas">
            <div className="px-4 pt-4"><SectionTitle right={<span className="text-[11px] text-slate-400">Clic en la partida para ver sus subpartidas</span>}>Avance por partida y subpartida</SectionTitle></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5">Partida / subpartida</th><th className="px-3 py-2.5 text-right">Presupuesto</th>
                  <th className="px-3 py-2.5 text-right">Ejecutado</th><th className="px-3 py-2.5 text-right">Por ejecutar</th>
                  <th className="px-3 py-2.5 text-right">Último periodo</th><th className="px-3 py-2.5">% avance</th></tr></thead>
                <tbody>{(data.partidas || []).map((p) => <PartidaFila key={p.id} p={p} />)}</tbody>
              </table>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5" testId="control-ranking">
              <SectionTitle>Partidas más rezagadas</SectionTitle>
              <div className="space-y-2.5">
                {ranking.slice(0, 5).map((p) => (
                  <div key={p.id} data-testid={`control-rezago-${p.id}`}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-600 font-semibold truncate pr-2">{p.nombre}</span>
                      <span className="text-slate-500">{pct(p.pct_avance)} · falta {mxn(p.por_ejecutar)}</span>
                    </div>
                    <ProgressBar value={Math.min((p.pct_avance || 0) * 100, 100)} />
                  </div>
                ))}
                {ranking.length === 0 && <p className="text-xs text-slate-400">Sin partidas con presupuesto.</p>}
              </div>
            </Card>

            <Card className="overflow-hidden" testId="control-tabla-periodos">
              <div className="px-4 pt-4"><SectionTitle>Ejecución por estimación</SectionTitle></div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[460px]">
                  <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2">No.</th><th className="px-3 py-2">Periodo</th>
                    <th className="px-3 py-2 text-right">$ periodo</th><th className="px-3 py-2 text-right">% del contrato</th>
                    <th className="px-3 py-2 text-right">Acumulado</th></tr></thead>
                  <tbody>
                    {(data.por_estimacion || []).map((e) => (
                      <tr key={e.numero} className="border-b border-slate-100" data-testid={`control-periodo-${e.numero}`}>
                        <td className="px-3 py-2 font-bold text-[#002B49]">{e.numero}</td>
                        <td className="px-3 py-2 text-slate-500">{fmtDate(e.periodo_inicio)} → {fmtDate(e.periodo_fin)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-[#00A3B5]">{mxn2(e.importe_periodo)}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{pct(e.pct_periodo)}</td>
                        <td className="px-3 py-2 text-right text-slate-600">{mxn2(e.importe_acumulado)}</td>
                      </tr>))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
